"use client"

import { useEffect } from "react"
import { useRouter } from "next/navigation"
import clsx from "clsx"
import { AnimatePresence } from "framer-motion"

import {
  type UnlikelyCart,
  type UnlikelyCustomAttribute,
  type UnlikelyCustomAttributeInput,
  type UnlikelyCustomAttributes,
} from "@unlikelystudio/commerce-connector"
import {
  cartDictionary,
  Line,
  Lines,
  TemplatesProvider,
  useRemoveCartLines,
} from "@unlikelystudio/react-ecommerce-hooks"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import { useLocale } from "~/lib/i18n/hooks/useLocale"
import {
  getProductCustomAttributesValue,
  getProductMetafieldValue,
  PRODUCT_CUSTOM_ATTRIBUTES,
  type BundleProductItemCustomAttribute,
  type ColorCustomAttribute,
  type ProductCustomAttributes,
} from "~/lib/shopify/constants"
import { handleZodSafeParse } from "~/lib/zod/utils/handle-zod-safe-parse"
import { useGetFreeProductInCart } from "~/data/free-products-in-cart/hook"
import { getFreeProductsEligibleInCart } from "~/data/free-products-in-cart/utils/get-free-products-eligible-in-cart"
import { useCartActions } from "~/hooks/cart/useCartActions"
import useGetCart from "~/hooks/cart/useGetCart"
import { Image, type ImageProps } from "~/components/ui/Image"
import { shopifyLinkSchema } from "~/components/ui/Link/_data/schema"
import serializeLink from "~/components/ui/Link/_data/serializer"
import { OPTION_COLOR, UNIVERSAL_COLOR_OPTION_VALUE } from "~/components/ui/Product/ProductHeader/_data/constants"
import type { EmplacementType } from "~/components/ui/Product/ProductHeader/PanelEmbroidery/EmbroideryForm/EmbroideryPlacementSelector/constants"
import { getProductTitle } from "~/components/ui/Product/utils/get-product-title"
import Icon from "~/components/abstracts/Icon"
import BottomFullCart from "~/components/globals/Cart/components/BottomFullCart"
import BundleProduct from "~/components/globals/Cart/components/CustomAttributeTemplate/BundleProduct"
import Embroidery from "~/components/globals/Cart/components/CustomAttributeTemplate/Embroidery"
import EmbroideryColor from "~/components/globals/Cart/components/CustomAttributeTemplate/EmbroideryColor"
import EmbroideryPicto from "~/components/globals/Cart/components/CustomAttributeTemplate/EmbroideryPicto"
import EmbroideryPlacement from "~/components/globals/Cart/components/CustomAttributeTemplate/EmbroideryPlacement"
import GiftMessage from "~/components/globals/Cart/components/CustomAttributeTemplate/GiftMessage"
import FreeDeliveryGauge from "~/components/globals/Cart/components/FreeDeliveryGauge"
import FreeProductsInCart from "~/components/globals/Cart/components/FreeProductsInCart"
import ColorOption from "~/components/globals/Cart/components/OptionTemplate/ColorOption"
import DefaultOption from "~/components/globals/Cart/components/OptionTemplate/DefaultOption"
import { serializeCartPayload } from "~/providers/GTMTrackingProvider/constants"
import { useTracking } from "~/providers/GTMTrackingProvider/hooks/use-tracking"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import type { TCart } from "~/managers/CartManager/_data/serializer"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

export type FullCartProps = PropsWithClassName<{
  toOffer: NonNullable<TCart>["toOffer"]
  freeDeliveryGauge: NonNullable<TCart>["freeDeliveryGauge"]
  productSlider: NonNullable<TCart>["productSlider"]
  giftPanel: NonNullable<TCart>["giftPanel"]
  globalEmbroideryPictos: NonNullable<TCart>["globalEmbroideryPictos"]
  setIsLoading: (isLoading: boolean) => void
}>

function FullCart({
  className,
  freeDeliveryGauge,
  globalEmbroideryPictos,
  setIsLoading,
  toOffer,
  productSlider,
  giftPanel,
}: FullCartProps) {
  const { cart } = useGetCart()
  const { sendEvent, sendResetEvent } = useTracking()
  const locale = useLocale()
  const t = useTranslate()

  const router = useRouter()

  const removeFreeProductsInCart = useRemoveFreeProductsInCart()

  const { onActionSuccess, isLoadingAction } = useCartActions()

  useEffect(() => {
    setIsLoading(isLoadingAction)
  }, [isLoadingAction])

  return (
    <div className={clsx(className, css.FullCart)}>
      <TemplatesProvider
        dictionary={{
          ...cartDictionary,
          it: {
            free: "Gratis",
            quantity: "Quantità",
            checkout: "Pagare",
            emptyCart: "Il tuo carrello è vuoto",
            remove: "Rimuovere",
            loading: "Caricamento...",
            totalPriceDescription: "Totale (IVA incl.)",
          },
        }}
        components={{
          line: (props) => {
            const isEmbroidery = getProductCustomAttributesValue(props.line?.attributes, "IS_EMBROIDERY_PRODUCT")
            const isCartGiftMessage = getProductCustomAttributesValue(props.line?.attributes, "GIFT_MESSAGE")

            // Free product added by the customer in the cart
            const isFreeProductInCart = getProductCustomAttributesValue(
              props?.line?.attributes,
              "IS_FREE_PRODUCT_IN_CART"
            )

            if (isEmbroidery) return <></>

            const uniqLineItemEmbroideryAttributes = getProductCustomAttributesValue(
              props.line?.attributes,
              "UNIQ_EMBROIDERY_ATTRIBUTES"
            )

            const embroideryLineItem = cart?.lines.find((lineItem) => {
              const uniqEmbroideryAttributes = getProductCustomAttributesValue(
                lineItem.attributes,
                "UNIQ_EMBROIDERY_ATTRIBUTES"
              )

              return (
                getProductCustomAttributesValue(lineItem.attributes, "IS_EMBROIDERY_PRODUCT") &&
                uniqEmbroideryAttributes === uniqLineItemEmbroideryAttributes
              )
            })

            const basePrice = props.line?.merchandise?.price?.amount ?? 0
            const embroideryPrice = embroideryLineItem?.merchandise?.price?.amount ?? 0

            const lineItemAmount = embroideryLineItem ? basePrice + embroideryPrice : basePrice
            const totalAmount = isFreeProductInCart ? props.line?.cost.totalAmount?.amount ?? 0 : lineItemAmount

            const selectedOptionsWithoutUniversalColor = props.line?.merchandise?.selectedOptions?.filter(
              (selectedOption) =>
                selectedOption?.name !== OPTION_COLOR && selectedOption?.value !== UNIVERSAL_COLOR_OPTION_VALUE
            )

            // Product Bundle Data
            const productBundleData = getProductCustomAttributesValue(props?.line?.attributes, "BUNDLE_PRODUCT")

            return (
              <Line
                {...props}
                classNames={{
                  ...props?.classNames,
                  selectQuantityContainerClassName: isFreeProductInCart ? sprinkles({ display: "none" }) : "",
                }}
                onUpdateCallbacks={{
                  async onSuccess(data, _, context) {
                    await onActionSuccess(data)
                    removeFreeProductsInCart({
                      currentCart: data.cart,
                      previousCart: context.previousCart?.cart,
                    })
                  },
                }}
                onRemoveCallbacks={{
                  async onSuccess(data, variables, context) {
                    await onActionSuccess(data)

                    const deletedLineItemId = variables[0]
                    const deletedLineItem = context.previousCart?.cart?.lines?.find(
                      (lineItem) => lineItem.id === deletedLineItemId
                    )

                    if (deletedLineItem) {
                      sendResetEvent()
                      sendEvent("remove_from_cart", {
                        ecommerce: serializeCartPayload({ ...data.cart, lines: [deletedLineItem] }),
                      })
                    }

                    removeFreeProductsInCart({
                      currentCart: data.cart,
                      previousCart: context.previousCart?.cart,
                    })
                  },
                }}
                onClick={(e, lineItem) => {
                  e.preventDefault()

                  const productLink = handleZodSafeParse(shopifyLinkSchema, lineItem?.merchandise?.product, {
                    errorContext: "productLink",
                  })

                  const foundLink = productLink ? serializeLink(productLink, locale, false) : false

                  if (foundLink) {
                    router.push(foundLink.href)
                  }
                }}
                multiplyPriceByQuantity
                additionalLinesToRemove={embroideryLineItem?.id ? [embroideryLineItem.id] : undefined}
                additionalLinesToUpdateQuantity={
                  embroideryLineItem?.id
                    ? [
                        {
                          id: embroideryLineItem.id,
                          attributes: embroideryLineItem?.attributes?.filter(isValidAttribute),
                        },
                      ]
                    : undefined
                }
                beforeOptionsChildren={ColorOptionComponent(props.line?.attributes)}
                line={
                  props.line
                    ? {
                        ...props.line,
                        merchandise: props?.line?.merchandise
                          ? {
                              ...props?.line?.merchandise,
                              image: props.line?.merchandise?.image ?? null,
                              selectedOptions: selectedOptionsWithoutUniversalColor,
                              price: {
                                currencyCode: props.line?.merchandise?.price?.currencyCode ?? "",
                                amount: totalAmount,
                              },
                              product: props.line?.merchandise?.product
                                ? {
                                    ...props.line?.merchandise?.product,
                                    title: getProductTitle(props.line?.merchandise?.product) ?? "",
                                  }
                                : null,
                            }
                          : null,
                      }
                    : undefined
                }
                optionTemplate={(option) => {
                  return productBundleData || isCartGiftMessage ? null : (
                    <DefaultOption className={css.optionLine} option={option} />
                  )
                }}
                customAttributeTemplate={(customAttribute) => {
                  switch (customAttribute.key) {
                    case PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_TEXT1:
                      return <Embroidery value={customAttribute.value ?? ""} count={1} />

                    case PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_PICTO1:
                      return (
                        <EmbroideryPicto
                          value={customAttribute.value ?? ""}
                          count={1}
                          pictos={globalEmbroideryPictos}
                        />
                      )

                    case PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_TEXT2:
                      return <Embroidery value={customAttribute.value ?? ""} count={2} />

                    case PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_PICTO2:
                      return (
                        <EmbroideryPicto
                          value={customAttribute.value ?? ""}
                          count={2}
                          pictos={globalEmbroideryPictos}
                        />
                      )

                    case PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_COLOR1:
                      return (
                        <EmbroideryColor color={customAttribute.value ?? ""} className={sprinkles({ marginTop: 4 })} />
                      )

                    case PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_EMP:
                      return (
                        <EmbroideryPlacement
                          placement={customAttribute.value}
                          placementType={getProductMetafieldValue<EmplacementType | "none">(
                            props.line?.merchandise?.product?.metafields,
                            "EMBROIDERY_EMPLACEMENT_TYPE"
                          )}
                        />
                      )

                    case PRODUCT_CUSTOM_ATTRIBUTES.BUNDLE_PRODUCT:
                      const bundeProductData = JSON.parse(
                        customAttribute.value as string
                      ) as BundleProductItemCustomAttribute[]

                      return bundeProductData.map((item, i) => (
                        <BundleProduct key={`product-bundle-item-${i}`} item={item} />
                      ))

                    case PRODUCT_CUSTOM_ATTRIBUTES.GIFT_MESSAGE:
                      const to = getProductCustomAttributesValue(props.line?.attributes, "GIFT_MESSAGE_TO")
                      const from = getProductCustomAttributesValue(props.line?.attributes, "GIFT_MESSAGE_FROM")
                      const message = customAttribute?.value ?? ""
                      return <GiftMessage message={message} to={to} from={from} />
                  }
                }}
              />
            )
          },
          img: (props) => {
            const image = props as ImageProps
            return (
              <Image
                {...image}
                height={100}
                width={85}
                asPlaceholder
                sizesFromBreakpoints={[{ ratio: "85px" }]}
                ratio={{ mobile: "50/60", tablet: "85/100" }}
                src={image.src as string}
                alt={image.alt}
              />
            )
          },
          select: (props) => {
            return (
              <div className={css.selectQuantityWrapper}>
                <select {...props} />
                <Icon name="ChevronDown" className={css.select} width={14} height={14} theme="blue-deep" />
              </div>
            )
          },
          button: (props) => {
            return (
              <button {...props} aria-label={t("aria_cart_remove_item")} className={css.buttonRemove}>
                <Icon name="Trash" theme="blue-deep" width={10} height={10} />
              </button>
            )
          },
        }}
      >
        <FreeDeliveryGauge freeDeliveryGauge={freeDeliveryGauge} />
        <Lines
          sortLineCallback={(a, b) => {
            const getAttr = (item: { attributes: UnlikelyCustomAttributes }, attr: keyof ProductCustomAttributes) =>
              getProductCustomAttributesValue(item.attributes, attr)

            const isFreeA = getAttr(a, "IS_FREE_PRODUCT_IN_CART")
            const isFreeB = getAttr(b, "IS_FREE_PRODUCT_IN_CART")
            const isCrossSellA = getAttr(a, "IS_FROM_CART_CROSS_SELL")
            const isCrossSellB = getAttr(b, "IS_FROM_CART_CROSS_SELL")

            // Priorité aux produits gratuits
            if (isFreeA !== isFreeB) return isFreeA ? 1 : -1

            // Priorité aux produits cross-sell
            if (isCrossSellA !== isCrossSellB) return isCrossSellA ? 1 : -1

            return 0
          }}
          classNames={{
            className: css.lineItemsList,
          }}
          lineProps={{
            customAttributesWhiteList: [
              PRODUCT_CUSTOM_ATTRIBUTES.MASTER_BUNDLE_PRODUCT,
              PRODUCT_CUSTOM_ATTRIBUTES.BUNDLE_PRODUCT,
              PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_COLOR,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_COLOR1,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_FONT,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_TEXT1,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_TEXT2,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_PICTO1,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_PICTO2,
              PRODUCT_CUSTOM_ATTRIBUTES.EMBROIDERY_EMP,
              PRODUCT_CUSTOM_ATTRIBUTES.UNIQ_EMBROIDERY_ATTRIBUTES,
              PRODUCT_CUSTOM_ATTRIBUTES.IS_EMBROIDERY_PRODUCT,
              PRODUCT_CUSTOM_ATTRIBUTES.IS_FROM_CART_CROSS_SELL,
              PRODUCT_CUSTOM_ATTRIBUTES.IS_FROM_POP_IN_CROSS_SELL,
              PRODUCT_CUSTOM_ATTRIBUTES.IS_FREE_PRODUCT_IN_CART,
              PRODUCT_CUSTOM_ATTRIBUTES.GIFT_MESSAGE_FROM,
              PRODUCT_CUSTOM_ATTRIBUTES.GIFT_MESSAGE_TO,
              PRODUCT_CUSTOM_ATTRIBUTES.GIFT_MESSAGE,
            ],
            editableOptions: false,
            classNames: {
              className: css.lineItem,
              titleClassName: css.title,
              imageClassName: css.image,
              selectQuantityLabelClassName: sprinkles({ display: "none" }),
              lineContentClassName: css.lineItemsContent,
              updateQuantityContainerClassName: css.updateQuantityContainer,
              selectQuantityClassName: css.selectQuantity,
              priceClassName: css.price,
              quantityClassName: css.customAttribute,
              optionClassName: css.option,
            },
          }}
        />
      </TemplatesProvider>

      <AnimatePresence>
        <FreeProductsInCart />
      </AnimatePresence>

      <BottomFullCart toOffer={toOffer} productSlider={productSlider} giftPanel={giftPanel} />
    </div>
  )
}

function ColorOptionComponent(attrs: UnlikelyCustomAttributes | undefined) {
  const productColor = attrs?.find((attr) => attr.key === PRODUCT_CUSTOM_ATTRIBUTES.PRODUCT_COLOR)
  const data = attrs && productColor && (JSON.parse(productColor.value as string) as ColorCustomAttribute)
  const productBundleData = attrs && getProductCustomAttributesValue(attrs, "BUNDLE_PRODUCT")
  const isBundle = productBundleData ? productBundleData.length > 0 : false

  // Universal color for all locales
  if (data?.value === UNIVERSAL_COLOR_OPTION_VALUE) {
    return null
  }

  return data && <ColorOption isBundle={isBundle} option={data} />
}

function isValidAttribute(attribute: UnlikelyCustomAttribute): attribute is UnlikelyCustomAttributeInput {
  return Boolean(attribute.key && attribute.value)
}

function useRemoveFreeProductsInCart() {
  const { sendEvent, sendResetEvent } = useTracking()
  const { cart } = useGetCart()
  const { data: freeProductsInCart } = useGetFreeProductInCart()
  const { mutate: removeCartLines } = useRemoveCartLines()

  function removeFreeProductsInCart({
    currentCart,
    previousCart,
  }: {
    currentCart: UnlikelyCart
    previousCart: Nullish<UnlikelyCart>
  }) {
    // Product can be added in the cart because they fill all the conditions
    const getFreeProductsAvailable = getFreeProductsEligibleInCart(freeProductsInCart, { cart: currentCart })

    // Filters out cart lines containing free products
    const freeProductsInCartAdded = currentCart?.lines?.filter(
      (line) => getProductCustomAttributesValue(line.attributes, "IS_FREE_PRODUCT_IN_CART") === "true"
    )

    const freeProductInCartNeedToDelete = freeProductsInCartAdded?.filter((freeProductInCartAdded) => {
      const productInCartNeedToStay = getFreeProductsAvailable?.find(
        (getFreeProductInCart) => freeProductInCartAdded.merchandise?.id === getFreeProductInCart.productVariant?.id
      )
      return !productInCartNeedToStay
    })

    if (freeProductInCartNeedToDelete?.length) {
      const lineIdsToRemove = freeProductInCartNeedToDelete.map((line) => line.id)

      const deletedLineItems = previousCart?.lines?.filter((lineItem) => lineIdsToRemove.includes(lineItem.id)) || []

      removeCartLines(lineIdsToRemove)
      sendResetEvent()

      sendEvent("remove_from_cart", {
        ecommerce: serializeCartPayload({
          ...currentCart,
          lines: deletedLineItems,
        }),
      })
    }
  }

  // Remove non eligible free products in cart when the cart is rendered
  useEffect(() => {
    if (cart) {
      removeFreeProductsInCart({
        currentCart: cart,
        previousCart: cart,
      })
    }
  }, [Boolean(cart)])

  return removeFreeProductsInCart
}

export default FullCart
